import groupChemicalsByOrder from 'utils/groupChemicalsByOrder';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Banner from 'components/Banner';
import PanelSection from 'components/PanelSection';

import useTranslation from './hooks/useTranslation';

import MixOrderGroup from './components/MixOrderGroup';
import MixOrderIssues from './components/MixOrderIssues';
import { SEVERITY_TO_BANNER_VARIANT, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const MixOrderAdvisoryPanel: FunctionComponent<Props> = ({
    className = '',
    hasError,
    isLoading,
    issues,
    orderedChemicals,
    testId = TEST_ID
}) => {
    const {
        EMPTY_MESSAGE,
        ERROR_MESSAGE,
        ISSUES_TITLE,
        LOADING,
        TITLE,
        UNKNOWN_FORMULATION,
        groupHeader,
        groupLabel,
        issueCodeTitle
    } = useTranslation();

    if (hasError) {
        return (
            <VerticalContainer
                className={`${styles.mixOrderAdvisoryPanel} ${className}`}
                testId={testId}>
                <Banner icon='warning' text={ERROR_MESSAGE} variant='warn' />
            </VerticalContainer>
        );
    }

    if (isLoading) {
        return (
            <VerticalContainer
                className={`${styles.mixOrderAdvisoryPanel} ${className}`}
                testId={testId}>
                <Wrapper className={styles.emptyMessage}>{LOADING}</Wrapper>
            </VerticalContainer>
        );
    }

    if (orderedChemicals.length === 0) {
        return (
            <VerticalContainer
                className={`${styles.mixOrderAdvisoryPanel} ${className}`}
                testId={testId}>
                <PanelSection text={TITLE}>
                    <Wrapper className={styles.emptyMessage}>{EMPTY_MESSAGE}</Wrapper>
                </PanelSection>
            </VerticalContainer>
        );
    }

    const groupedByOrder = groupChemicalsByOrder(orderedChemicals);
    const orderedGroupKeys = Array.from(groupedByOrder.keys()).sort((a, b) => a - b);

    return (
        <VerticalContainer
            className={`${styles.mixOrderAdvisoryPanel} ${className}`}
            testId={testId}>
            <PanelSection text={TITLE} />

            {orderedGroupKeys.map(order => (
                <MixOrderGroup
                    header={groupHeader(order, groupLabel(order))}
                    issues={issues}
                    items={groupedByOrder.get(order) ?? []}
                    key={order}
                    unknownFormulationText={UNKNOWN_FORMULATION}
                />
            ))}

            {issues.length > 0 && (
                <MixOrderIssues
                    describeIssueCode={issueCodeTitle}
                    issues={issues}
                    severityToBannerVariant={SEVERITY_TO_BANNER_VARIANT}
                    title={ISSUES_TITLE}
                />
            )}
        </VerticalContainer>
    );
};

export default MixOrderAdvisoryPanel;
